// The notices that are full-width bars above the page in the top-bar layout,
// and where each one lives with the sidebar on. The page column then shows
// the page only:
//
//   - the state of the member's own profile (not active yet, hidden) goes on
//     the profile card at the top of the sidebar;
//   - one card in the sidebar foot, the first that applies: a team
//     invitation, the upgrade call;
//   - the time zone has its own row, see useTimezoneSync;
//   - the result of a click ("Saved") is a toast, see useToast.
//
// A phone has no sidebar. Its Home page shows the first notice as one bar
// (MobileHomeNotice), the More tab has a dot, and the More sheet shows the
// same cards as the sidebar.
import { computed } from 'vue'
import { usePage } from '@inertiajs/vue3'
import { useCurrentUser } from '@/shared/composables/useCurrentUser'
import { usePermissionSystem } from '@/shared/composables/usePermissionSystem'
import { useUserProfileCompletion } from '@/shared/composables/useUserProfileCompletion.js'
import {
  ACCESS_TYPE,
  MENTOR_STATUS,
  PROFILE_STATUS,
  ROLE,
  TEAM_ROLE,
} from '@/shared/data/permissionSystem'
import { useTimezoneSync } from '@/shared/composables/useTimezoneSync'
import { useFeatureFlags } from '@/shared/composables/useFeatureFlags'

export type UpgradeKind = 'solo' | 'owner' | 'member'

export type TeamInvite = { teamName: string; ownerName: string | null }

export type ProfileStatus = {
  kind: 'incomplete' | 'hidden'
  title: string
  // The share of the profile that is done, for "incomplete" only.
  percent: number | null
  text: string | null
  action: string
  href: string
}

export type FootCard = 'invite' | 'upgrade'

export const useShellNotices = () => {
  const page = usePage()
  const { currentUser } = useCurrentUser()
  const { hasAccess, hasRole, hasTeamRole, isProfileStatus, isMentorStatus } =
    usePermissionSystem()
  const { progressInPercent, firstItemToComplete } = useUserProfileCompletion()
  const { ask: timezoneAsk } = useTimezoneSync()

  // The new mentee experience has no profile step: the chat wrote the
  // profile, so no "Account not active" notice.
  const { NEW_MENTEE_EXPERIENCE_ENABLED } = useFeatureFlags()
  const profileIncomplete = computed(
    () =>
      !NEW_MENTEE_EXPERIENCE_ENABLED.value &&
      hasAccess(ACCESS_TYPE.GROWTHMENTOR) &&
      isProfileStatus(PROFILE_STATUS.NOT_COMPLETE),
  )
  const profileHidden = computed(
    () =>
      hasRole(ROLE.MENTOR) &&
      isMentorStatus(MENTOR_STATUS.HIDDEN_FROM_AUTO_DECLINE),
  )

  // The server names the team. With no name (the top-bar layout sends no
  // sidebar prop) the texts say "a team".
  const teamInvite = computed<TeamInvite | null>(() => {
    if (!hasTeamRole(TEAM_ROLE.INVITED)) return null
    return (
      (page.props.shared as { sidebar?: { teamInvite?: TeamInvite | null } })
        .sidebar?.teamInvite ?? { teamName: '', ownerName: null }
    )
  })

  // A team invite and a mentor applicant in the funnel get no upgrade call.
  const upgradeKind = computed<UpgradeKind | null>(() => {
    if (!hasAccess(ACCESS_TYPE.NONE)) return null
    if (hasTeamRole(TEAM_ROLE.INVITED)) return null
    if (currentUser.value.mentorApplicantInFunnel) return null
    if (hasTeamRole(TEAM_ROLE.OWNER)) return 'owner'
    if (hasTeamRole(TEAM_ROLE.MEMBER)) return 'member'
    return 'solo'
  })

  // The profile card shows one state: the profile has to be complete before
  // it can go live.
  const profileStatus = computed<ProfileStatus | null>(() => {
    const next = firstItemToComplete.value
    if (profileIncomplete.value && next) {
      return {
        kind: 'incomplete',
        title: 'Account not active',
        percent: progressInPercent.value,
        text: null,
        action: next.description,
        href: next.href,
      }
    }
    if (profileHidden.value) {
      return {
        kind: 'hidden',
        title: 'Your profile is hidden',
        percent: null,
        text: 'Turn your session times on to go live.',
        action: 'Go live',
        href: '/settings/account/sessions',
      }
    }
    return null
  })

  // One card in the sidebar foot.
  const footCard = computed<FootCard | null>(() => {
    if (teamInvite.value) return 'invite'
    if (upgradeKind.value) return 'upgrade'
    return null
  })

  // The dot on the phone's More tab: something asks for the member. The
  // upgrade call does not count.
  const needsAttention = computed(
    () => !!profileStatus.value || !!teamInvite.value || !!timezoneAsk.value,
  )

  return {
    profileIncomplete,
    profileHidden,
    profileStatus,
    teamInvite,
    upgradeKind,
    footCard,
    timezoneAsk,
    needsAttention,
  }
}
